import { Alert, Card, Space, Tag, Typography } from 'antd';
import {
  CalendarOutlined,
  FileTextOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { AuthGuard } from '~/app/auth/guard/AuthGuard';
import { PlanDetailTaskSubtasks } from '~/app/components/maintenance/preventive/PlanDetailPageContainer/components/PlanDetailTaskSubtasks/PlanDetailTaskSubtasks';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import {
  formatTicketDueDate,
  priorityTagColor,
  shortPlanCode,
  ticketDueColor,
  ticketDueUrgency,
  ticketStatusTagColor,
  ticketTypeTagColor,
} from '../WorkOrderKanbanPageContainer/utils';
import { TicketDetailTimeline } from './components/TicketDetailTimeline/TicketDetailTimeline';
import { TicketDetailVisits } from './components/TicketDetailVisits/TicketDetailVisits';
import { useCurativeTicketIncidentRedirect } from './hooks/useCurativeTicketIncidentRedirect';
import { useTicketDetailPage } from './hooks/useTicketDetailPage';

const { Title, Paragraph } = Typography;

function TicketDetailPageView() {
  const { t } = useTranslation('maintenance');
  const page = useTicketDetailPage();

  useCurativeTicketIncidentRedirect({
    ticketType: page.ticket?.type,
    incidentId: page.ticket?.incidentId ?? null,
    isLoading: page.isLoading,
  });

  if (page.isError) {
    return (
      <Alert type="error" message={t('workOrders.ticketDetail.loadFailed')} />
    );
  }

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <Link to="/maintenance/work-orders/kanban">
        {t('workOrders.ticketDetail.back')}
      </Link>

      <div>
        <Title level={2} style={{ marginBottom: 8 }}>
          {page.ticket?.code ?? t('workOrders.ticketDetail.loading')}
        </Title>
        {page.ticket ? (
          <Space wrap>
            <Tag color={ticketTypeTagColor(page.ticket.type)}>
              {t(`workOrders.ticketType.${page.ticket.type}`)}
            </Tag>
            <Tag color={priorityTagColor(page.ticket.priority)}>
              {t(`workOrders.priority.${page.ticket.priority}`)}
            </Tag>
            <Tag color={ticketStatusTagColor(page.ticket.status)}>
              {t(`workOrders.ticketStatus.${page.ticket.status}`)}
            </Tag>
            {page.ticket.needsFollowUp ? (
              <Tag color="gold">{t('workOrders.kanban.needsFollowUp')}</Tag>
            ) : null}
            {(() => {
              const dueLabel = formatTicketDueDate(page.ticket.dueAt);
              const dueUrgency = ticketDueUrgency(page.ticket.dueAt);
              if (!dueLabel || !dueUrgency) return null;
              return (
                <Tag
                  icon={<CalendarOutlined />}
                  style={{
                    margin: 0,
                    color: ticketDueColor(dueUrgency),
                    borderColor: ticketDueColor(dueUrgency),
                    background: 'transparent',
                    fontWeight: 600,
                  }}
                >
                  {dueUrgency === 'overdue'
                    ? t('workOrders.kanban.dueOverdue', { date: dueLabel })
                    : dueUrgency === 'soon'
                      ? t('workOrders.kanban.dueSoon', { date: dueLabel })
                      : t('workOrders.kanban.dueLabel', { date: dueLabel })}
                </Tag>
              );
            })()}
          </Space>
        ) : null}
      </div>

      {page.ticket?.needsFollowUp ? (
        <Alert
          type="warning"
          showIcon
          message={t('workOrders.ticketDetail.followUpBanner')}
          description={t('workOrders.ticketDetail.followUpHint')}
        />
      ) : null}

      {page.ticket?.activeWorkOrderStatus === 'ON_HOLD' ? (
        <Alert
          type="info"
          showIcon
          message={t('workOrders.ticketDetail.onHoldBanner', {
            code: page.ticket.activeWorkOrderCode ?? '—',
          })}
          description={
            page.ticket.activeOnHoldReason?.trim()
              ? t('workOrders.ticketDetail.onHoldReason', {
                  reason: page.ticket.activeOnHoldReason.trim(),
                })
              : t('workOrders.ticketDetail.onHoldNoReason')
          }
        />
      ) : null}

      <Card loading={page.isLoading}>
        {page.ticket?.planName ? (
          <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
            {t('workOrders.kanban.planNameLabel', {
              name: page.ticket.planName,
            })}
          </Typography.Text>
        ) : null}
        {page.ticket?.planCode ? (
          <Typography.Text
            type="secondary"
            title={page.ticket.planCode}
            style={{ display: 'block', marginBottom: 8 }}
          >
            {t('workOrders.kanban.planCodeLabel', {
              code: shortPlanCode(page.ticket.planCode),
            })}
          </Typography.Text>
        ) : null}
        <Title level={5}>{page.ticket?.title}</Title>
        {page.ticket?.description ? (
          <Paragraph>{page.ticket.description}</Paragraph>
        ) : (
          <Paragraph type="secondary">
            {t('workOrders.ticketDetail.noDescription')}
          </Paragraph>
        )}
        {page.ticket && page.ticket.subtaskNames.length > 0 ? (
          <div style={{ marginTop: 16 }}>
            <PlanDetailTaskSubtasks
              taskId={page.ticket.taskId ?? page.ticket.id}
              subtaskNames={page.ticket.subtaskNames}
            />
          </div>
        ) : null}
      </Card>

      <Card
        title={
          <Space>
            <FileTextOutlined style={{ color: WORK_ORDER_UI.primary }} />
            <span>{t('workOrders.ticketDetail.visits.title')}</span>
          </Space>
        }
        styles={{ body: { paddingTop: 12 } }}
      >
        <TicketDetailVisits visits={page.visits} loading={page.isLoading} />
      </Card>

      <Card
        title={
          <Space>
            <HistoryOutlined style={{ color: WORK_ORDER_UI.primary }} />
            <span>{t('workOrders.ticketDetail.historyTitle')}</span>
          </Space>
        }
        styles={{ body: { paddingTop: 12 } }}
      >
        <TicketDetailTimeline
          events={page.events}
          actorNameById={page.actorNameById}
          loading={page.isLoading}
        />
      </Card>
    </Space>
  );
}

export function TicketDetailPageContainer() {
  return (
    <AuthGuard requiredPermissions={[PermissionKey.WORK_ORDER_READ]}>
      <TicketDetailPageView />
    </AuthGuard>
  );
}
